import { normalizeOriginPathUrl } from '@vlt/shared';
import type { CommonPayload, CtaTarget, Logger, Transport } from './types';
import type { VideoTracker } from './video';

export const CTA_SELECTOR =
  '[data-vlt-cta-id], a[href], button, [role="button"], input[type="submit"], input[type="button"]';

type CtaTrackerOptions = {
  videoTracker: Pick<VideoTracker, 'markCtaClicked'>;
  transport: Pick<Transport, 'sendCtaEvent'>;
  getCommonPayload: () => CommonPayload;
  targets?: CtaTarget[];
  logger?: Pick<Logger, 'debug'>;
};

type ResolvedCta =
  | { kind: 'legacy'; ctaId: string; ctaName: string | null }
  | { kind: 'managed'; target: CtaTarget };

function isElementWithTextMatch(element: HTMLElement): boolean {
  return element.matches('a, button, [role="button"], input[type="submit"], input[type="button"]');
}

function getElementMatchText(element: HTMLElement): string | null {
  if (
    element instanceof HTMLInputElement &&
    (element.type === 'submit' || element.type === 'button')
  ) {
    return element.value.trim();
  }

  return element.textContent?.trim() ?? null;
}

export function createCtaTracker(options: CtaTrackerOptions) {
  const attached = new WeakSet<HTMLElement>();
  let managedTargets = options.targets ?? [];

  function resolveManagedUrl(element: HTMLElement): CtaTarget | null {
    if (!(element instanceof HTMLAnchorElement)) return null;
    const normalizedHref = normalizeOriginPathUrl(element.href, { pageHref: window.location.href });
    if (!normalizedHref) return null;

    return (
      managedTargets.find((target) => {
        return target.match_type === 'url' && target.pattern === normalizedHref;
      }) ?? null
    );
  }

  function resolveManagedText(element: HTMLElement): CtaTarget | null {
    if (!isElementWithTextMatch(element)) return null;
    const text = getElementMatchText(element);
    if (!text) return null;

    return (
      managedTargets.find((target) => {
        return target.match_type === 'text' && target.pattern === text;
      }) ?? null
    );
  }

  function resolveCta(element: HTMLElement): ResolvedCta | null {
    const legacyId = element.dataset.vltCtaId?.trim();
    if (legacyId) {
      return {
        kind: 'legacy',
        ctaId: legacyId,
        ctaName: element.dataset.vltCtaName ?? null,
      };
    }

    const urlTarget = resolveManagedUrl(element);
    if (urlTarget) return { kind: 'managed', target: urlTarget };

    const textTarget = resolveManagedText(element);
    if (textTarget) return { kind: 'managed', target: textTarget };

    return null;
  }

  function attach(element: HTMLElement): void {
    if (attached.has(element)) return;
    const resolved = resolveCta(element);
    if (!resolved) return;

    attached.add(element);
    element.addEventListener('click', () => {
      if (resolved.kind === 'legacy') {
        options.videoTracker.markCtaClicked(element, resolved.ctaId, resolved.ctaName);
        return;
      }

      const common = options.getCommonPayload();
      options.transport.sendCtaEvent({
        ...common,
        cta_id: resolved.target.cta_id,
        cta_label: resolved.target.label || null,
        match_type: resolved.target.match_type,
        occurred_at: new Date().toISOString(),
      });
    });
  }

  function attachAll(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>(CTA_SELECTOR).forEach(attach);
  }

  function setManagedTargets(targets: CtaTarget[]): void {
    managedTargets = targets;
    attachAll();
    options.logger?.debug('cta_targets.attached', { count: targets.length });
  }

  function observe(): MutationObserver | null {
    if (!('MutationObserver' in window)) return null;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches(CTA_SELECTOR)) attach(node);
          node.querySelectorAll<HTMLElement>(CTA_SELECTOR).forEach(attach);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    return observer;
  }

  return { attach, attachAll, observe, setManagedTargets };
}
